// src/stores/devicesStore.js
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import config from "../config";
import logger from "../utils/logger";

/**
 * Devices Store - Zustand-based device state management
 *
 * Manages:
 * - Device list (basic device info: deviceId, serialNumber, displayName)
 * - Device states (lockState, lockdownState, connectivity, battery)
 * - Selected device ID
 * - Loading states for devices and device states
 * - Error states
 *
 * Features:
 * - Cache-first device loading from DynamoDB via backend
 * - Optimistic updates for device actions (lock, unlock, freeze, etc.)
 * - Auto-selection of first device or newly claimed devices
 * - Orphaned device cleanup when devices are removed
 * - Bulk lockdown state tracking
 *
 * Migration Note:
 * This store replaces device-related state from AppContent.js
 * See docs/ZUSTAND_MIGRATION_GUIDE.md for migration details
 */

const useDevicesStore = create(
  devtools(
    persist(
      (set, get) => ({
        // --- State ---
        devices: [],
        deviceStates: {}, // Map of deviceId -> device state object
        selectedDeviceId: null,
        isLoading: false,
        isLoadingStates: false,
        isRefreshing: false,
        isSyncing: false,
        error: null,
        bulkDeviceLockdownStates: {}, // Map of deviceId -> lockdown state for bulk operations
        connectivityStatusUpdateTimes: (() => {
          // Load from localStorage on init
          const saved = localStorage.getItem("connectivityStatusUpdateTimes");
          return saved ? JSON.parse(saved) : {};
        })(), // Map of deviceId -> ISO timestamp of last connectivity update
        eventDeniedAlerts: {}, // Map of deviceId -> boolean for Event Denied alert state

        // --- Computed Getters ---

        /**
         * Get the currently selected device object
         * @returns {Object|null} Selected device or null
         */
        getSelectedDevice: () => {
          const { devices, selectedDeviceId } = get();
          return devices.find((d) => d.deviceId === selectedDeviceId) || null;
        },

        /**
         * Get device state for a specific device
         * @param {string} deviceId - Device ID
         * @returns {Object|null} Device state or null
         */
        getDeviceState: (deviceId) => {
          const { deviceStates } = get();
          return deviceStates[deviceId] || null;
        },

        /**
         * Get selected device's state
         * @returns {Object|null} Selected device state or null
         */
        getSelectedDeviceState: () => {
          const { selectedDeviceId, deviceStates } = get();
          return selectedDeviceId
            ? deviceStates[selectedDeviceId] || null
            : null;
        },

        /**
         * Get devices for a specific installation
         * @param {string} installationId - Installation ID
         * @returns {Array} Filtered devices
         */
        getDevicesByInstallation: (installationId) => {
          const { devices } = get();
          return devices.filter((d) => d.installationId === installationId);
        },

        // --- Actions ---

        /**
         * Set devices list
         * @param {Array|Function} devices - Array of device objects, or callback function
         */
        setDevices: (devices) => {
          if (typeof devices === "function") {
            // Support callback pattern: setDevices((prevDevices) => newDevices)
            set(
              (state) => {
                const newDevices = devices(state.devices);
                if (!Array.isArray(newDevices)) {
                  logger.error(
                    "[dataRefreshed] prevDevices is not an array, skipping display name merge"
                  );
                  return state; // Return unchanged state
                }
                return { devices: newDevices };
              },
              false,
              "setDevices"
            );
          } else {
            // Support array pattern: setDevices([...])
            if (!Array.isArray(devices)) {
              logger.error("Attempted to set non-array devices:", devices);
              return;
            }
            set({ devices }, false, "setDevices");
          }
        },

        /**
         * Set device states
         * @param {Object|Function} deviceStates - Map of deviceId -> state, or callback function
         */
        setDeviceStates: (deviceStates) => {
          if (typeof deviceStates === "function") {
            // Support callback pattern: setDeviceStates((prevStates) => newStates)
            set(
              (state) => ({ deviceStates: deviceStates(state.deviceStates) }),
              false,
              "setDeviceStates"
            );
          } else {
            // Support object pattern: setDeviceStates({...})
            set({ deviceStates }, false, "setDeviceStates");
          }
        },

        /**
         * Set selected device ID with validation
         * @param {string|null} deviceId - Device ID to select
         */
        setSelectedDeviceId: (deviceId) => {
          const { devices } = get();

          // Validate device exists before setting
          if (deviceId && !devices.find((d) => d.deviceId === deviceId)) {
            logger.warn(`Attempted to select non-existent device: ${deviceId}`);
            return;
          }

          set({ selectedDeviceId: deviceId }, false, "setSelectedDeviceId");
          logger.log(`Selected device: ${deviceId}`);
        },

        /**
         * Update device state for a specific device
         * @param {string} deviceId - Device ID
         * @param {Object} stateUpdate - Partial state to merge
         */
        updateDeviceState: (deviceId, stateUpdate) => {
          set(
            (state) => ({
              deviceStates: {
                ...state.deviceStates,
                [deviceId]: {
                  ...(state.deviceStates[deviceId] || {}),
                  ...stateUpdate,
                },
              },
            }),
            false,
            "updateDeviceState"
          );
        },

        /**
         * Update multiple device states at once
         * @param {Array} updates - Array of {deviceId, state} objects
         */
        updateMultipleDeviceStates: (updates) => {
          set(
            (state) => {
              const newDeviceStates = { ...state.deviceStates };
              updates.forEach(({ deviceId, state: stateUpdate }) => {
                newDeviceStates[deviceId] = {
                  ...(newDeviceStates[deviceId] || {}),
                  ...stateUpdate,
                };
              });
              return { deviceStates: newDeviceStates };
            },
            false,
            "updateMultipleDeviceStates"
          );
        },

        /**
         * Set bulk device lockdown states
         * @param {Object|Function} bulkStates - Map of deviceId -> lockdownState, or callback function
         */
        setBulkDeviceLockdownStates: (bulkStates) => {
          if (typeof bulkStates === "function") {
            // Support callback pattern: setBulkDeviceLockdownStates((prevStates) => newStates)
            set(
              (state) => ({
                bulkDeviceLockdownStates: bulkStates(
                  state.bulkDeviceLockdownStates
                ),
              }),
              false,
              "setBulkDeviceLockdownStates"
            );
          } else {
            // Support object pattern: setBulkDeviceLockdownStates({...})
            set(
              { bulkDeviceLockdownStates: bulkStates },
              false,
              "setBulkDeviceLockdownStates"
            );
          }
        },

        /**
         * Update bulk lockdown state for a device
         * @param {string} deviceId - Device ID
         * @param {string} lockdownState - Lockdown state value
         */
        updateBulkLockdownState: (deviceId, lockdownState) => {
          set(
            (state) => ({
              bulkDeviceLockdownStates: {
                ...state.bulkDeviceLockdownStates,
                [deviceId]: lockdownState,
              },
            }),
            false,
            "updateBulkLockdownState"
          );
        },

        /**
         * Fetch devices from backend (cache-first)
         * @param {string} installationId - Installation ID filter
         * @param {Function} authenticatedFetch - Authenticated fetch function
         * @returns {Promise<Array>} Array of devices
         */
        fetchDevices: async (installationId, authenticatedFetch) => {
          set({ isLoading: true, error: null }, false, "fetchDevices:start");

          try {
            logger.log("Fetching device list...");
            const response = await authenticatedFetch(
              `${config.apiEndpoint}/devices`,
              {
                headers: { "x-api-key": config.apiKey },
              }
            );

            if (!response.ok) {
              if (response.status === 403) {
                throw new Error(
                  "Access forbidden. Please contact administrator for support."
                );
              }
              throw new Error(`Failed to fetch devices: ${response.status}`);
            }

            const allDevices = await response.json();

            // Ensure allDevices is an array
            if (!Array.isArray(allDevices)) {
              logger.error(
                "API returned non-array devices response:",
                allDevices
              );
              throw new Error("Invalid devices response from server");
            }

            // Filter by installation if provided
            const deviceList = installationId
              ? allDevices.filter((d) => d.installationId === installationId)
              : allDevices;

            // Sort devices consistently by display name or serial number
            deviceList.sort((a, b) => {
              const nameA = a.displayName || a.serialNumber || a.deviceId;
              const nameB = b.displayName || b.serialNumber || b.deviceId;
              return nameA.localeCompare(nameB);
            });

            logger.log(
              `Fetched ${deviceList.length} devices for installation ${installationId}`
            );

            // Auto-select first device if none selected
            const { selectedDeviceId, devices: prevDevices } = get();
            const prevDeviceIds = new Set(prevDevices.map((d) => d.deviceId));
            const newDevices = deviceList.filter(
              (d) => !prevDeviceIds.has(d.deviceId)
            );

            let newSelectedId = selectedDeviceId;

            // Auto-select newly claimed device
            if (!selectedDeviceId && newDevices.length > 0) {
              newSelectedId = newDevices[0].deviceId;
              logger.log(
                `Auto-selected newly claimed device: ${newSelectedId}`
              );
            }
            // Auto-select first device if this is the first load
            else if (!selectedDeviceId && deviceList.length > 0) {
              newSelectedId = deviceList[0].deviceId;
              logger.log(`Auto-selected first device: ${newSelectedId}`);
            }
            // Validate current selection still exists
            else if (
              selectedDeviceId &&
              !deviceList.find((d) => d.deviceId === selectedDeviceId)
            ) {
              newSelectedId =
                deviceList.length > 0 ? deviceList[0].deviceId : null;
              logger.log(
                `Previous selection invalid, auto-selected: ${newSelectedId}`
              );
            }

            set(
              {
                devices: deviceList,
                selectedDeviceId: newSelectedId,
                isLoading: false,
                error: null,
              },
              false,
              "fetchDevices:success"
            );

            // Clean up orphaned device states
            get().cleanupOrphanedStates(deviceList.map((d) => d.deviceId));

            return deviceList;
          } catch (error) {
            logger.error("Failed to fetch devices:", error);
            const errorMessage = error.message || "Failed to fetch devices";
            set(
              {
                isLoading: false,
                error: errorMessage,
              },
              false,
              "fetchDevices:error"
            );
            throw error;
          }
        },

        /**
         * Fetch device state for a specific device (write mode, no cache)
         * @param {string} deviceId - Device ID
         * @param {Function} authenticatedFetch - Authenticated fetch function
         * @returns {Promise<Object|null>} Device state or null
         */
        fetchDeviceState: async (deviceId, authenticatedFetch) => {
          if (!deviceId) return null;

          try {
            logger.log(`Fetching state for device ${deviceId}...`);
            const response = await authenticatedFetch(
              `${config.apiEndpoint}/device-state?deviceId=${deviceId}`,
              { headers: { "x-api-key": config.apiKey } }
            );

            if (!response.ok) {
              throw new Error(
                `Failed to fetch device state: ${response.status}`
              );
            }

            const state = await response.json();

            // Update state in store
            get().updateDeviceState(deviceId, state);

            return state;
          } catch (error) {
            logger.error(
              `Failed to fetch state for device ${deviceId}:`,
              error
            );
            return null;
          }
        },

        /**
         * Fetch device states for all devices
         * @param {Function} authenticatedFetch - Authenticated fetch function
         * @returns {Promise<void>}
         */
        fetchAllDeviceStates: async (authenticatedFetch) => {
          const { devices } = get();
          logger.log(
            `[fetchAllDeviceStates] Starting fetch for ${devices.length} devices`
          );
          set({ isLoadingStates: true }, false, "fetchAllDeviceStates:start");

          try {
            const statePromises = devices.map((device) =>
              fetch(
                `${config.apiEndpoint}/device-state-readonly?deviceId=${device.deviceId}`,
                {
                  headers: { "x-api-key": config.apiKey },
                }
              )
                .then(async (res) => {
                  if (!res.ok)
                    throw new Error(
                      `Failed to fetch state for ${device.deviceId}`
                    );
                  const state = await res.json();
                  logger.log(
                    `[fetchAllDeviceStates] Fetched state for device ${device.deviceId}`
                  );
                  return { deviceId: device.deviceId, state };
                })
                .catch((error) => {
                  logger.error(
                    `Failed to fetch state for device ${device.deviceId}:`,
                    error
                  );
                  return { deviceId: device.deviceId, state: null };
                })
            );

            const results = await Promise.all(statePromises);

            // Update all device states
            const updates = results
              .filter((r) => r.state !== null)
              .map((r) => ({ deviceId: r.deviceId, state: r.state }));

            logger.log(
              `[fetchAllDeviceStates] Updating ${updates.length} device states`
            );
            get().updateMultipleDeviceStates(updates);

            set(
              { isLoadingStates: false },
              false,
              "fetchAllDeviceStates:success"
            );
            logger.log(`[fetchAllDeviceStates] Completed successfully`);
          } catch (error) {
            logger.error("Failed to fetch all device states:", error);
            set(
              { isLoadingStates: false },
              false,
              "fetchAllDeviceStates:error"
            );
          }
        },

        /**
         * Refresh devices and their states
         * @param {string} installationId - Installation ID
         * @param {Function} authenticatedFetch - Authenticated fetch function
         * @returns {Promise<void>}
         */
        refreshDevices: async (installationId, authenticatedFetch) => {
          logger.log(
            `[refreshDevices] Starting refresh for installation ${installationId}`
          );
          set({ isRefreshing: true }, false, "refreshDevices:start");

          try {
            logger.log(`[refreshDevices] Step 1: Fetching devices...`);
            const deviceList = await get().fetchDevices(
              installationId,
              authenticatedFetch
            );
            logger.log(
              `[refreshDevices] Step 1 complete: ${
                deviceList?.length || 0
              } devices fetched`
            );

            logger.log(
              `[refreshDevices] Step 2: Fetching all device states...`
            );
            await get().fetchAllDeviceStates(authenticatedFetch);
            logger.log(
              `[refreshDevices] Step 2 complete: Device states fetched`
            );

            const { deviceStates, devices } = get();
            logger.log(
              `[refreshDevices] Final state: ${devices.length} devices, ${
                Object.keys(deviceStates).length
              } device states`
            );

            set({ isRefreshing: false }, false, "refreshDevices:success");
            logger.log(`[refreshDevices] Refresh completed successfully`);
          } catch (error) {
            logger.error("[refreshDevices] Failed to refresh devices:", error);
            set({ isRefreshing: false }, false, "refreshDevices:error");
            throw error;
          }
        },

        /**
         * Update device display name
         * @param {string} deviceId - Device ID
         * @param {string} displayName - New display name
         * @param {Function} authenticatedFetch - Authenticated fetch function
         * @returns {Promise<boolean>} Success status
         */
        updateDeviceDisplayName: async (
          deviceId,
          displayName,
          authenticatedFetch
        ) => {
          try {
            logger.log(
              `Updating display name for device ${deviceId} to "${displayName}"`
            );

            const response = await authenticatedFetch(
              `${config.apiEndpoint}/update-display-name`,
              {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                  "x-api-key": config.apiKey,
                },
                body: JSON.stringify({ deviceId, displayName }),
              }
            );

            if (!response.ok) {
              throw new Error(
                `Failed to update display name: ${response.status}`
              );
            }

            // Optimistically update device in store
            set(
              (state) => ({
                devices: state.devices.map((device) =>
                  device.deviceId === deviceId
                    ? { ...device, displayName }
                    : device
                ),
              }),
              false,
              "updateDeviceDisplayName"
            );

            logger.log(
              `Display name updated successfully for device ${deviceId}`
            );
            return true;
          } catch (error) {
            logger.error("Failed to update device display name:", error);
            return false;
          }
        },

        /**
         * Execute device action (lock, unlock, freeze, etc.)
         * @param {string} deviceId - Device ID
         * @param {string} actionType - Action type (LOCK, UNLOCK, FREEZE, UNFREEZE, etc.)
         * @param {Function} authenticatedFetch - Authenticated fetch function
         * @returns {Promise<Object>} Response data
         */
        executeDeviceAction: async (
          deviceId,
          actionType,
          authenticatedFetch
        ) => {
          if (!deviceId) {
            throw new Error("No device ID provided");
          }

          logger.log(`Executing ${actionType} on device ${deviceId}`);

          try {
            const response = await authenticatedFetch(
              `${config.apiEndpoint}/device-action`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "x-api-key": config.apiKey,
                },
                body: JSON.stringify({ deviceId, actionType }),
              }
            );

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              throw new Error(
                errorData.error || `Device action failed: ${response.status}`
              );
            }

            const data = await response.json();

            // Optimistic update based on action type
            const optimisticUpdates = {
              LOCK: { lockState: "LOCKED" },
              UNLOCK: { lockState: "UNLOCKED" },
              FREEZE: { lockdownState: "FREEZE" },
              UNFREEZE: { lockdownState: "NORMAL" },
              SCHEDULE_FREEZE: { lockdownState: "FREEZE" },
              SCHEDULE_UNFREEZE: { lockdownState: "NORMAL" },
            };

            if (optimisticUpdates[actionType]) {
              get().updateDeviceState(deviceId, optimisticUpdates[actionType]);
            }

            logger.log(
              `${actionType} executed successfully on device ${deviceId}`
            );
            return data;
          } catch (error) {
            logger.error(
              `Failed to execute ${actionType} on device ${deviceId}:`,
              error
            );
            throw error;
          }
        },

        /**
         * Execute bulk lockdown action on multiple devices
         * @param {string} installationId - Installation ID
         * @param {string} actionType - Action type (SCHEDULE_FREEZE, SCHEDULE_UNFREEZE)
         * @param {Array<string>} deviceIds - Optional array of device IDs to target
         * @param {Function} authenticatedFetch - Authenticated fetch function
         * @returns {Promise<Object>} Response data
         */
        executeBulkLockdown: async (
          installationId,
          actionType,
          deviceIds,
          authenticatedFetch
        ) => {
          logger.log(
            `Executing bulk ${actionType} on installation ${installationId}`
          );

          try {
            const body = { installationId, actionType };
            if (deviceIds && deviceIds.length > 0) {
              body.deviceIds = deviceIds;
            }

            const response = await authenticatedFetch(
              `${config.apiEndpoint}/bulk-lockdown`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "x-api-key": config.apiKey,
                },
                body: JSON.stringify(body),
              }
            );

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              throw new Error(
                errorData.error || `Bulk lockdown failed: ${response.status}`
              );
            }

            const data = await response.json();
            logger.log(`Bulk ${actionType} executed successfully`);
            return data;
          } catch (error) {
            logger.error(`Failed to execute bulk ${actionType}:`, error);
            throw error;
          }
        },

        /**
         * Clean up device states for devices that no longer exist
         * @param {Array<string>} currentDeviceIds - Array of current device IDs
         */
        cleanupOrphanedStates: (currentDeviceIds) => {
          const { deviceStates, bulkDeviceLockdownStates } = get();
          const currentIdSet = new Set(currentDeviceIds);

          // Find orphaned device state IDs
          const orphanedStateIds = Object.keys(deviceStates).filter(
            (id) => !currentIdSet.has(id)
          );

          // Find orphaned bulk lockdown state IDs
          const orphanedBulkIds = Object.keys(bulkDeviceLockdownStates).filter(
            (id) => !currentIdSet.has(id)
          );

          if (orphanedStateIds.length > 0 || orphanedBulkIds.length > 0) {
            set(
              (state) => {
                const newDeviceStates = { ...state.deviceStates };
                const newBulkStates = { ...state.bulkDeviceLockdownStates };

                orphanedStateIds.forEach((id) => {
                  delete newDeviceStates[id];
                  logger.log(`🧹 Removed orphaned deviceState for ${id}`);
                });

                orphanedBulkIds.forEach((id) => {
                  delete newBulkStates[id];
                  logger.log(
                    `🧹 Removed orphaned bulkDeviceLockdownState for ${id}`
                  );
                });

                return {
                  deviceStates: newDeviceStates,
                  bulkDeviceLockdownStates: newBulkStates,
                };
              },
              false,
              "cleanupOrphanedStates"
            );
          }
        },

        /**
         * Clear all device state (reset)
         */
        clearDevices: () => {
          set(
            {
              devices: [],
              deviceStates: {},
              selectedDeviceId: null,
              bulkDeviceLockdownStates: {},
              isLoading: false,
              isLoadingStates: false,
              isRefreshing: false,
              error: null,
            },
            false,
            "clearDevices"
          );
        },

        /**
         * Set syncing state
         * @param {boolean} isSyncing - Syncing status
         */
        setIsSyncing: (isSyncing) => {
          set({ isSyncing }, false, "setIsSyncing");
        },

        /**
         * Set connectivity status update time for a device
         * @param {Function} updater - Function that receives previous times and returns new times
         */
        setConnectivityStatusUpdateTime: (updater) => {
          set(
            (state) => {
              const newTimes =
                typeof updater === "function"
                  ? updater(state.connectivityStatusUpdateTimes)
                  : updater;

              // Persist to localStorage
              try {
                localStorage.setItem(
                  "connectivityStatusUpdateTimes",
                  JSON.stringify(newTimes)
                );
              } catch (e) {
                logger.error("Failed to save connectivity status times:", e);
              }

              return { connectivityStatusUpdateTimes: newTimes };
            },
            false,
            "setConnectivityStatusUpdateTime"
          );
        },

        /**
         * Set Event Denied alert state for a device
         * @param {string} deviceId - Device ID
         * @param {boolean} show - Whether to show the alert
         */
        setEventDeniedAlert: (deviceId, show) => {
          set(
            (state) => ({
              eventDeniedAlerts: {
                ...state.eventDeniedAlerts,
                [deviceId]: show,
              },
            }),
            false,
            "setEventDeniedAlert"
          );
        },
      }),
      {
        name: "devices-storage",
        // Only persist selectedDeviceId to avoid stale device data
        partialize: (state) => ({
          selectedDeviceId: state.selectedDeviceId,
        }),
      }
    ),
    { name: "DevicesStore" }
  )
);

export default useDevicesStore;
